export function valuePresent(value: any) {
  return value !== undefined && value !== null && value !== '';
}

export function numericCount(value: any) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function safeObject(value: any): Record<string, any> {
  if (!value) return {};

  if (typeof value === 'object' && !Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  return {};
}

export function getRunResponseMetadata(row: any): Record<string, any> {
  return safeObject(row?.response_metadata);
}

export function getCurrentGenerationRun(row: any) {
  return (
    row?.current_generation_run ||
    row?.best_generation_run ||
    row?.final_generation_run ||
    row?.generation_run ||
    row
  );
}

export function rootGenerationRunIdOf(row: any) {
  const current = getCurrentGenerationRun(row);

  return (
    row?.root_generation_run_id ||
    row?.original_generation_run_id ||
    row?.initial_generation_run_id ||
    current?.root_generation_run_id ||
    current?.original_generation_run_id ||
    current?.initial_generation_run_id ||
    row?.root_generation_run?.id ||
    row?.original_generation_run?.id ||
    row?.initial_generation_run?.id ||
    current?.id ||
    row?.current_generation_run_id ||
    row?.id
  );
}

export function currentGenerationRunIdOf(row: any) {
  const current = getCurrentGenerationRun(row);

  return (
    row?.current_generation_run_id ||
    row?.latest_generation_run_id ||
    row?.final_generation_run_id ||
    row?.best_generation_run_id ||
    current?.id ||
    row?.id
  );
}

export function getGenerationRunValidationState(row: any) {
  const current = getCurrentGenerationRun(row);
  const responseMetadata = getRunResponseMetadata(current);

  const validationRunId =
    row?.latest_validation_run_id ||
    row?.validation_run_id ||
    row?.last_validation_run_id ||
    current?.latest_validation_run_id ||
    current?.validation_run_id ||
    current?.last_validation_run_id ||
    responseMetadata.latest_validation_run_id ||
    responseMetadata.validation_run_id ||
    responseMetadata.last_validation_run_id ||
    null;

  const validationRun =
    row?.latest_validation_run ||
    row?.validation_run ||
    row?.last_validation_run ||
    current?.latest_validation_run ||
    current?.validation_run ||
    current?.last_validation_run ||
    responseMetadata.latest_validation_run ||
    responseMetadata.validation_run ||
    responseMetadata.last_validation_run ||
    null;

  const validationStatus =
    row?.latest_validation_status ||
    row?.validation_status ||
    row?.last_validation_status ||
    current?.latest_validation_status ||
    current?.validation_status ||
    current?.last_validation_status ||
    responseMetadata.validation_status ||
    responseMetadata.latest_validation_status ||
    responseMetadata.last_validation_status ||
    null;

  const validationScore =
    row?.latest_validation_score ??
    row?.validation_score ??
    current?.latest_validation_score ??
    current?.validation_score ??
    responseMetadata.validation_score ??
    responseMetadata.latest_validation_score ??
    null;

  const publishableThreshold =
    row?.publishable_threshold ??
    current?.publishable_threshold ??
    responseMetadata.publishable_threshold ??
    null;

  const needsReviewThreshold =
    row?.needs_review_threshold ??
    current?.needs_review_threshold ??
    responseMetadata.needs_review_threshold ??
    null;

  const validationRunCount =
    numericCount(row?.validation_run_count) ||
    numericCount(row?.validation_runs_count) ||
    numericCount(row?.validation_count) ||
    numericCount(row?.validations_count) ||
    numericCount(row?.total_validation_runs) ||
    numericCount(current?.validation_run_count) ||
    numericCount(current?.validation_runs_count) ||
    numericCount(responseMetadata.validation_run_count) ||
    numericCount(responseMetadata.validation_runs_count);

  const validationRuns = Array.isArray(row?.validation_runs)
    ? row.validation_runs
    : Array.isArray(row?.validations)
      ? row.validations
      : Array.isArray(current?.validation_runs)
        ? current.validation_runs
        : Array.isArray(current?.validations)
          ? current.validations
          : Array.isArray(responseMetadata.validation_runs)
            ? responseMetadata.validation_runs
            : Array.isArray(responseMetadata.validations)
              ? responseMetadata.validations
              : [];

  const explicitValidationStatuses = new Set([
    'validated',
    'validation_failed',
    'validation_passed',
    'validation_error',
    'needs_review',
    'publishable',
    'passed',
    'failed',
    'regenerated',
    'regeneration_failed',
  ]);

  const explicitTopLevelValidationStatuses = new Set([
    'validation_failed',
    'validation_passed',
    'validation_error',
    'needs_review',
    'publishable',
    'validated',
    'regenerated',
    'regeneration_failed',
  ]);

  const validationStatusCandidates = [
    validationStatus,
    responseMetadata.status,
  ]
    .filter(valuePresent)
    .map((value) => String(value).toLowerCase());

  const topLevelStatusCandidates = [
    row?.status,
    current?.status,
  ]
    .filter(valuePresent)
    .map((value) => String(value).toLowerCase());

  const hasValidationStatus =
    validationStatusCandidates.some((status) => explicitValidationStatuses.has(status)) ||
    topLevelStatusCandidates.some((status) => explicitTopLevelValidationStatuses.has(status));

  const hasValidationRun =
    validationRunCount > 0 ||
    validationRuns.length > 0 ||
    valuePresent(validationRunId) ||
    Boolean(validationRun && typeof validationRun === 'object' && valuePresent(validationRun.id)) ||
    Boolean(validationRun && typeof validationRun !== 'object');

  const hasValidationScore = valuePresent(validationScore);

  return {
    hasValidation: hasValidationRun || hasValidationStatus || hasValidationScore,
    validationRunId,
    validationRun,
    validationStatus,
    validationScore,
    publishableThreshold,
    needsReviewThreshold,
    validationRunCount,
    validationRuns,
    responseMetadata,
  };
}

export function generationRunHasValidation(row: any) {
  return getGenerationRunValidationState(row).hasValidation;
}